import React, { useEffect, useState } from 'react';
import { DotStyles } from '#/common/components/Dot';
import styled from 'styled-components';
import { ACTIVE_COLOR, ACTIVE_MARKER_Z_INDEX } from './Styles';

export interface ActiveMarkerProps {
  selectedIndex: number;
  menuItemsRefs: React.RefObject<HTMLDivElement>[];
  isCollapsed?: boolean;
}

const MARKER_SIZE = 14;

const StyledMarker = styled.div<{ top: number; left: number; visible: boolean; animated: boolean }>`
  ${DotStyles({
    size: MARKER_SIZE,
    color: ACTIVE_COLOR,
  })}

  z-index: ${ACTIVE_MARKER_Z_INDEX} !important;
  position: absolute;
  top: 0;
  left: ${(props) => props.left}px;
  transition: ${({ animated }) =>
    animated ? 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s ease' : 'none'};
  transform: translateY(${(props) => props.top}px);
  opacity: ${(props) => (props.visible ? 1 : 0)};
`;

const ActiveMarker: React.FC<ActiveMarkerProps> = ({
  selectedIndex,
  menuItemsRefs,
  isCollapsed,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);
  const [animated, setAnimated] = useState(false);

  // Initial fade-in effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Track the panel edge in real-time during the collapse/expand transition
  useEffect(() => {
    setAnimated(false);

    const TRANSITION_MS = 320;
    const startTime = performance.now();
    let rafId: number;

    const tick = () => {
      const selectedRef = menuItemsRefs[selectedIndex];
      if (selectedRef?.current) {
        const element = selectedRef.current;
        const rect = element.parentElement?.getBoundingClientRect();
        const menuRect = element.closest('[data-menu-container]')?.getBoundingClientRect();
        if (menuRect && rect) {
          setPosition({
            top: rect.top - menuRect.top + rect.height / 2 - MARKER_SIZE / 2,
            left: menuRect.width - MARKER_SIZE / 2,
          });
        }
      }

      if (performance.now() - startTime < TRANSITION_MS) {
        rafId = requestAnimationFrame(tick);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isCollapsed]);

  useEffect(() => {
    const calculatePosition = () => {
      const selectedRef = menuItemsRefs[selectedIndex];

      if (selectedRef?.current) {
        const element = selectedRef.current;
        const rect = element.parentElement?.getBoundingClientRect();
        const menuRect = element.closest('[data-menu-container]')?.getBoundingClientRect();

        if (menuRect && rect) {
          // Calculate center position
          const top = rect.top - menuRect.top + rect.height / 2 - MARKER_SIZE / 2;
          const left = menuRect.width - MARKER_SIZE / 2;

          setPosition({ top, left });
        }
      }
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);

    return () => window.removeEventListener('resize', calculatePosition);
  }, [selectedIndex, menuItemsRefs]);

  return (
    <StyledMarker top={position.top} left={position.left} visible={visible} animated={animated} />
  );
};

export default ActiveMarker;
